import { BodyText, Icon, TooltipIconButton, useConfirmDialog, useIfUser } from '@if/mui'
import { Box, Stack } from '@mui/material'
import HideUnreadMessagePopupsCheckbox from '@src/components/HideUnreadMessagePopupsCheckbox'
import { useUnreadCreditApplicationIds } from '@src/data/api/chat-message/chat-message-api'
import {
  clearUnreadMessagePopupDismissal,
  isUnreadMessagePopupDismissed,
  markUnreadMessagePopupDismissed,
  useHideUnreadMessagePopups,
} from '@src/hooks/unreadMessagePopup-hooks'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export const useUnreadMessagesPopup = (creditApplication: CreditApplication) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useIfUser()
  const { id, financingProgramId } = creditApplication
  const hasUnread = useUnreadCreditApplicationIds().has(id)
  const [popupsHidden] = useHideUnreadMessagePopups()

  const dialog = useConfirmDialog({
    mode: 'info',
    title: t('common.unreadMessages'),
    message: ({ close }) => (
      <>
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <TooltipIconButton
            title={t('common.close')}
            aria-label={t('common.close')}
            onClick={() => close()}
            size="small"
          >
            <Icon icon={X} size="sm" />
          </TooltipIconButton>
        </Box>
        <Stack spacing={1}>
          <BodyText>{t('viewCreditApplication.unreadMessagesDescription')}</BodyText>
          <HideUnreadMessagePopupsCheckbox />
        </Stack>
      </>
    ),
    confirmLabel: t('common.messages'),
    hideCancel: true,
    onConfirm: () => {
      navigate(`/chat-dashboard/${financingProgramId}/${id}?from=application`)
    },
  })

  const closingProgrammatically = useRef(false)

  useEffect(() => {
    if (!hasUnread) {
      clearUnreadMessagePopupDismissal(id)
      if (dialog.isOpen) {
        closingProgrammatically.current = true
        dialog.close()
      }
      return
    }

    if (user?.isBannerUser || popupsHidden || isUnreadMessagePopupDismissed(id)) return

    closingProgrammatically.current = false
    void dialog.open().then((confirmed) => {
      if (!confirmed && !closingProgrammatically.current) markUnreadMessagePopupDismissed(id)
    })
  }, [hasUnread, id, user?.isBannerUser, popupsHidden, dialog])
}
